import { Colors, Lightning } from '@lightningjs/sdk';
import Tile, { TileTemplateSpec } from 'components/common/tiles/Tile';
import { Channel, Episode, Show } from 'types/api/media';
import { getFontFaceFromStyle, withSeparator } from 'support/textUtils';
import RatingBadge from 'components/common/RatingBadge';
import {
  getDurationHourMinutesNoSpaceFormatted,
  getSeasonEpisodeFormatted,
  isChannel,
  isEpisode,
  isMovie,
  isSeries,
  isShow,
} from 'support/contentUtils';
import { translate } from 'support/translate';
import { SEARCH_TILE_FADE } from 'support/animations';
import { getImageTextureObj } from 'support/generalUtils';
import TextLabel from '../TextLabel';

const TILE_INFO_OFFSET_Y = 10;

const TILE_INFO_TEXT_STYLE: Lightning.textures.TextTexture.Settings = {
  maxLines: 1,
};

const SERIES_GENRES_LIMIT = 3;

export interface SearchTileTemplateSpec extends TileTemplateSpec {
  data: Channel | Episode | Show;

  FocusedOverlay: {
    OverlayLabelContainer: {
      GoToIcon: Lightning.textures.ImageTexture;
      SelectLabel: typeof TextLabel;
    };
  };
  MediaInfo: {
    MediaType: typeof TextLabel;
    Title: typeof TextLabel;
    Metadata: {
      Rating: typeof RatingBadge;
      Others: typeof TextLabel;
    };
  };
}

export default abstract class SearchTile
  extends Tile<SearchTileTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<SearchTileTemplateSpec>
{
  private _FocusedOverlay = this.getByRef('FocusedOverlay')!;

  private _OverlayLabelContainer = this._FocusedOverlay.getByRef(
    'OverlayLabelContainer',
  )!;
  private _SelectLabel = this._OverlayLabelContainer.getByRef('SelectLabel')!;

  protected _MediaInfo = this.getByRef('MediaInfo')!;

  private _MediaType = this._MediaInfo.getByRef('MediaType')!;
  private _Title = this._MediaInfo.getByRef('Title')!;
  private _Metadata = this._MediaInfo.getByRef('Metadata')!;

  private _Rating = this._Metadata.getByRef('Rating')!;
  private _Others = this._Metadata.getByRef('Others')!;

  // To be overridden by subclasses:
  static override get width() {
    return 0;
  }

  static override get height() {
    return 0;
  }

  static override get highlightWidth() {
    return 0;
  }

  static override get highlightHeight() {
    return 0;
  }

  override get title() {
    return [
      this._MediaType.text?.text ?? '',
      this._data?.title ?? '',
      this._Rating.text?.text ?? '',
      this._Others.text?.text ?? '',
      this._SelectLabel.text?.text ?? '',
    ];
  }

  override get action() {
    if (!this._data) return super.action;

    return isChannel(this._data) || isShow(this._data) ? 'details' : 'play';
  }

  override set data(data: SearchTileTemplateSpec['data']) {
    super.data = data;

    this.updateOverlayLabel();
    this.updateMediaInfo();
  }

  set hideMediaInfo(val: boolean) {
    this._MediaInfo.patch({ visible: !val });
  }

  static override _template(): Lightning.Component.Template<SearchTileTemplateSpec> {
    return {
      ...super._template(),
      FocusedOverlay: {
        w: this.width,
        h: this.highlightHeight,
        rect: true,
        transitions: SEARCH_TILE_FADE,
        color: Colors('background').alpha(0.8).get(),
        alpha: 0,
        OverlayLabelContainer: {
          x: w => w / 2,
          y: h => h / 2,
          mount: 0.5,
          flex: {
            direction: 'row',
            alignItems: 'center',
          },
          GoToIcon: {
            y: -3,
            ...getImageTextureObj(
              'static/images/arrow-right-light.svg',
              26,
              22,
            ),
            flexItem: { marginRight: 13 },
          },
          SelectLabel: {
            type: TextLabel,
            text: {
              fontSize: 21,
              fontFace: getFontFaceFromStyle('bold'),
              wordWrapWidth: this.width,
              ...TILE_INFO_TEXT_STYLE,
            },
          },
        },
      },
      MediaInfo: {
        y: this.highlightHeight + TILE_INFO_OFFSET_Y,
        flex: { direction: 'column' },
        MediaType: {
          type: TextLabel,
          color: Colors('text').get(),
          text: {
            fontSize: 21,
            fontFace: getFontFaceFromStyle('regular'),
            wordWrapWidth: this.width,
            ...TILE_INFO_TEXT_STYLE,
          },
        },
        Title: {
          type: TextLabel,
          color: Colors('text').get(),
          text: {
            fontSize: 28,
            fontFace: getFontFaceFromStyle('bold'),
            wordWrapWidth: this.width,
            ...TILE_INFO_TEXT_STYLE,
          },
        },
        Metadata: {
          flex: { direction: 'row' },
          Rating: {
            type: RatingBadge,
            size: 'small',
            flexItem: { marginRight: 7 },
            badgeColor: Colors('text').get(),
          },
          Others: {
            type: TextLabel,
            color: Colors('text').get(),
            text: {
              fontSize: 21,
              fontFace: getFontFaceFromStyle('regular'),
              wordWrapWidth: this.width,
              ...TILE_INFO_TEXT_STYLE,
            },
          },
        },
      },
    };
  }

  override _focus() {
    this._FocusedOverlay.setSmooth('alpha', 1);
  }

  override _unfocus() {
    this._FocusedOverlay.setSmooth('alpha', 0);
  }

  private updateOverlayLabel() {
    let label = '';
    if (isChannel(this._data)) {
      label = translate('search.goToChannel');
    } else if (isEpisode(this._data)) {
      label = translate('search.goToEpisode');
    } else if (isSeries(this._data)) {
      label = translate('search.goToSeries');
    } else if (isMovie(this._data)) {
      label = translate('search.goToMovie');
    }
    this._SelectLabel.patch({ text: { text: label } });
  }

  private updateMediaInfo() {
    this._Title.patch({ text: { text: this._data.title } });

    let mediaType = '';
    let otherMetadata = '';
    let rating = '';

    if (isChannel(this._data)) {
      const channel = this._data as Channel;

      mediaType = translate('search.channel');
      otherMetadata = channel.description || '';
    } else if (isShow(this._data)) {
      const show = this._data as Show;
      const isMediaSeries = isSeries(this._data);

      mediaType = isMediaSeries
        ? translate('search.series')
        : translate('search.movie');
      otherMetadata = isMediaSeries
        ? this.getSeriesMetadataText(show)
        : this.getMovieMetadataText(show);
      rating = show.rating || '';
    } else if (isEpisode(this._data)) {
      const episode = this._data as Episode;
      const seasonEpisodeText = getSeasonEpisodeFormatted(episode, ' ');

      mediaType = `${seasonEpisodeText} ${episode.seriesName}`;
      otherMetadata = this.getEpisodeMetadataText(episode);
      rating = episode.rating || '';
    }

    this._MediaType.patch({ text: { text: mediaType } });
    this._Others.patch({ text: { text: otherMetadata } });
    this._Rating.patch({ rating: rating });
  }

  private getMovieMetadataText(movie: Show) {
    return withSeparator(
      movie.releaseYear,
      getDurationHourMinutesNoSpaceFormatted(movie),
      movie.genres[0] ?? '',
    );
  }

  private getSeriesMetadataText(series: Show) {
    return series.genres.slice(0, SERIES_GENRES_LIMIT).join(', ');
  }

  private getEpisodeMetadataText(episode: Episode) {
    return withSeparator(
      getDurationHourMinutesNoSpaceFormatted(episode),
      episode.genre ?? '',
    );
  }
}
